import { TextArea } from '@quintype/em/components/text-area';
import camelCase from 'lodash/camelCase';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  updateBackupConfig,
  updateConfigVersion,
  updateHaveUnsavedChanges,
  updateHistoryActionStatus,
  updatePBConfig
} from '../../actions/builder-action';
import { updateCustomCodeGeneralSettings } from '../../actions/custom-code-action';
import { checkValidEncodedCustomCode, encodeCustomCode, saveConfig } from '../../utils/utils';
import { CopyIcon } from '../localization/copy';
import styles from './custom-code-general-settings.module.css';
import { get } from 'lodash';
import { Button } from '@quintype/em/components/button';
import { IParams } from '../../utils/interfaces';
import { useHistory, useParams } from 'react-router-dom';
import { Close } from '../../atoms/close-svg';
import { ConfirmationPopup } from '../confirmation-popup/index';
import { DisplayMessage } from '../display-message';
import CwvAnalyserPanel from './cwv-analyser/CwvAnalyserPanel';

interface IStore {
  builder: {
    config: {
      general: {
        tags?: {
          [key: string]: {
            insertToHead?: '';
            insertToBody?: '';
          };
        };
      };
    };
  };
}

const CustomCodeGeneralSettings = () => {
  const [currentTab, setCurrentTab] = useState('All Pages');
  const [codeTab, setCodeTab] = useState<'head' | 'body'>('head');
  const [showSaveMsg, setShowSaveMsg] = useState(false);
  const [showErrMsg, setShowErrMsg] = useState(false);
  const [showConfirmationPopup, toggleConfirmationPopup] = useState(false);
  const [showGoToTop, setShowGoToTop] = useState(false);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [analysisCompletedFor, setAnalysisCompletedFor] = useState<{ [key: string]: boolean }>({});
  const [cwvPanelWidth, setCwvPanelWidth] = useState(330);
  const runAnalyserRef = useRef<{ run: () => void; stop: () => void } | null>(null);
  const textareaContainerRef = useRef<HTMLDivElement | null>(null);
  const getAnalysisKey = (pageTab: string, context: 'head' | 'body') => `${pageTab}--${context}`;
  const currentAnalysisKey = getAnalysisKey(currentTab, codeTab);
  const isAnalysisCompletedForCurrentTab = analysisCompletedFor[currentAnalysisKey] ?? false;

  useEffect(() => {
    const handleScroll = () => setShowGoToTop(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const dispatch = useDispatch();
  const history = useHistory();
  const { publisherId = '', domain } = useParams<IParams>();

  const tagConfig = useSelector((state: any) => get(state, ['builder', 'config', 'general', 'tags'], {}));

  const pageConvention = (tab: string) => camelCase(tab);

  const { insertToHead = '', insertToBody = '' } = get(tagConfig, [pageConvention(currentTab)], {});
  const hasCodeInCurrentTab = !!(codeTab === 'head' ? insertToHead : insertToBody).trim();

  const { config, haveUnsavedChanges, backupConfig, editControl } = useSelector(state => get(state, ['builder']));
  const hasEditAccess = get(editControl, ['access'], false);
  const cwvEnabled = useSelector((state: any) => get(state, ['builder', 'config', 'featureToggle', 'cwvAnalyser'], true));
  const needsAnalysisBeforeSave = cwvEnabled && !isAnalysisCompletedForCurrentTab && hasCodeInCurrentTab;
  const updateSettings = (key: string, value: string, currentValue: string) => {
    dispatch(
      updateCustomCodeGeneralSettings({
        [pageConvention(currentValue)]: {
          ...tagConfig[pageConvention(currentTab)],
          ...{ [key]: encodeCustomCode(value) }
        }
      })
    );
    dispatch(updateHaveUnsavedChanges(true));
    setAnalysisCompletedFor(prev => ({
      ...prev,
      [currentAnalysisKey]: false
    }));
  };

  const handleAnalysisStatusChange = useCallback((running: boolean) => {
    setIsAnalysing(running);
    if (!running) {
      setAnalysisCompletedFor(prev => ({
        ...prev,
        [currentAnalysisKey]: true
      }));
    }
  }, [currentAnalysisKey]);

  const handleIssueCodeClick = (issueCode: string) => {
    const container = textareaContainerRef.current;
    if (!container) return;
    const textarea = container.querySelector('textarea');
    if (!textarea) return;
    const value = textarea.value;
    const index = value.indexOf(issueCode);
    if (index === -1) return;
    textarea.focus();
    textarea.setSelectionRange(index, index + issueCode.length);
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10) || 18;
    const linesBeforeMatch = value.substring(0, index).split('\n').length - 1;
    textarea.scrollTop = linesBeforeMatch * lineHeight;
  };

  const shortCode = [
    '[page-type]',
    '[story-slug]',
    '[story-id]',
    '[story-type]',
    '[primary-section]',
    '[sections]',
    '[sections-name]',
    '[sections-display-name]',
    '[authors-id]',
    '[authors]'
  ];

  const copyTextToClipboard = (text: string | null) => {
    const copyFrom = document.createElement('textarea');
    copyFrom.textContent = text;
    document.body.appendChild(copyFrom);
    copyFrom.select();
    document.execCommand('copy');
    copyFrom.blur();
    document.body.removeChild(copyFrom);
  };

  const supportedPages = [
    'All Pages',
    'Home Page',
    'Section Page',
    'Story Page',
    'Magazine Landing Page',
    'Issue Landing Page',
    'Magazine Archive Page',
    'Tag Page',
    'Search Page'
  ];

  const tabStyles = (page: string) => (currentTab === page ? styles.activeTab : styles.tabs);

  const navigateToHome = () => {
    history.push(`/accounts/${publisherId}/${domain}`);
  };

  const onActionButtonClick = async (navigate: boolean = true) => {
    const nextVersion = config.version + 1;
    const updatedConfig = { ...config, version: nextVersion };
    const response = await saveConfig(publisherId, updatedConfig, domain);
    if (response?.activeVersion && response?.actionButtonStatus) {
      const { activeVersion = 0, actionButtonStatus } = response;
      batch(() => {
        dispatch(updateConfigVersion(activeVersion));
        dispatch(updateHistoryActionStatus(actionButtonStatus));
        dispatch(updateBackupConfig({ config }));
        dispatch(updateHaveUnsavedChanges(false));
      });
      setShowSaveMsg(true);
      setTimeout(() => setShowSaveMsg(false), 1500);
    } else {
      setShowErrMsg(true);
      setTimeout(() => setShowErrMsg(false), 1500);
    }
    toggleConfirmationPopup(false);
    if (navigate) {
      navigateToHome();
    }
  };

  const onCancel = () => toggleConfirmationPopup(false);

  const onDiscard = () => {
    dispatch(updatePBConfig(backupConfig));
    toggleConfirmationPopup(false);
    dispatch(updateHaveUnsavedChanges(false));
    navigateToHome();
  };

  const onClose = () => {
    if (haveUnsavedChanges) {
      toggleConfirmationPopup(true);
    } else {
      navigateToHome();
    }
  };

  const insertCode = (label: string, tag: string, value: string, key: string) => (
    <TextArea
      label={label}
      placeholder={`Paste any valid HTML code here. The code will be inserted to the end of ${tag} section, right before ${tag}`}
      value={checkValidEncodedCustomCode(value)}
      onChange={(data: string) => updateSettings(key, data, currentTab)}
      disabled={!hasEditAccess}
    />
  );

  return (
    <div
      className={styles.tagsGeneralSettingsWrapper}
      style={{ gridTemplateColumns: `17% 1fr${cwvEnabled ? ` ${cwvPanelWidth}px` : ''}` }}
    >
      <div className={styles.pageNav}>
        <h4 className={styles.pageNavTitle}>Insert code</h4>
        {supportedPages.map(page => (
          <div onClick={() => setCurrentTab(page)} key={page} className={tabStyles(page)}>
            {page}
          </div>
        ))}
      </div>

      <div className={styles.editorHeader}>
        <div>
          <h3 className={styles.editorTitle}>Insert Code in {currentTab}</h3>
        </div>
        <div className={styles.actionBar}>
          {hasEditAccess && cwvEnabled && (
            <Button
              type={isAnalysing ? 'default' : 'primary'}
              disabled={!isAnalysing && !hasCodeInCurrentTab}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                (e.currentTarget as HTMLButtonElement).blur();
                if (isAnalysing) {
                  runAnalyserRef.current?.stop();
                  setIsAnalysing(false);
                } else {
                  runAnalyserRef.current?.run();
                  setIsAnalysing(true);
                }
              }}
            >
              {isAnalysing ? 'Stop Analyser' : 'Run Analyser'}
            </Button>
          )}
          {hasEditAccess && (
            <span
              className={needsAnalysisBeforeSave ? styles.saveTooltipWrapper : undefined}
              data-tooltip="Please run the analyser before saving"
            >
              <Button
                type="default"
                onClick={() => onActionButtonClick(false)}
                disabled={needsAnalysisBeforeSave}
              >
                Save
              </Button>
            </span>
          )}
          <button
            className={styles.closeIconBtn}
            onClick={onClose}
            title="Close custom code settings"
            aria-label="Close custom code settings"
          >
            <Close />
          </button>
        </div>
      </div>

      <div className={styles.editorColumn}>
        <div className={styles.codeTabs}>
          <button
            className={codeTab === 'head' ? styles.codeTabActive : styles.codeTab}
            onClick={() => setCodeTab('head')}
          >
            Insert to &lt;head&gt;
          </button>
          <button
            className={codeTab === 'body' ? styles.codeTabActive : styles.codeTab}
            onClick={() => setCodeTab('body')}
          >
            Insert to &lt;body&gt;
          </button>
        </div>

        <div ref={textareaContainerRef}>
          {codeTab === 'head' && insertCode('Insert to head', '</head>', insertToHead, 'insertToHead')}
          {codeTab === 'body' && insertCode('Insert to body', '</body>', insertToBody, 'insertToBody')}
        </div>

        <div className={styles.shortCodeTitle}>Shortcodes</div>
        <div className={styles.shortCodeMsg}>
          Copy-paste the shortcodes below to integrate the variables in your code
        </div>
        <div className={styles.shortCodeTemplate}>
          {shortCode.map((shortCodeItem: string, shortCodeIndex: number) => {
            return (
              <div className={styles.copyWrapper} key={shortCodeIndex}>
                <span>{shortCodeItem}</span>
                <button className={styles.copyButton} onClick={() => copyTextToClipboard(shortCodeItem)}>
                  <CopyIcon />
                  <span>Copy Shortcode</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {cwvEnabled && (
        <CwvAnalyserPanel
          code={codeTab === 'head'
            ? checkValidEncodedCustomCode(insertToHead)
            : checkValidEncodedCustomCode(insertToBody)
          }
          context={codeTab}
          pageTab={currentTab}
          publisherId={publisherId}
          runRef={runAnalyserRef}
          onStatusChange={handleAnalysisStatusChange}
          onWidthChange={setCwvPanelWidth}
          onIssueCodeClick={handleIssueCodeClick}
        />
      )}
      {showGoToTop && (
        <button
          className={styles.goToTopBtn}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          title="Go to top"
          aria-label="Go to top"
        >
          &#8679;
        </button>
      )}
      {showConfirmationPopup && (
        <ConfirmationPopup onCancel={onCancel} onSave={onActionButtonClick} onDiscard={onDiscard} />
      )}
      {showSaveMsg && <DisplayMessage />}
      {showErrMsg && (
        <DisplayMessage customMessage="Oops, server error countered! Changes made were not saved, please try again later!" />
      )}
    </div>
  );
};

export default CustomCodeGeneralSettings;
